import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, IconButton, InputAdornment } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { Form, Formik } from "formik";
import * as yup from "yup";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { text } from "../../text";
import { hooks } from "../../hooks";
import { theme } from "../../constants";
import { components } from "../../components";
import AppTextField from "../../components/apps/AppTextField";
import { AppSubmitButton } from "../../components/apps";
import { LOGIN } from "../../graphql/mutations/auth";
import { APP_ROUTES } from "../../constants/app_routes";
import { useAuthMethod } from "../../hooks/useAuth";
import { showErrorToast } from "../../components/apps/AppError";

export const SigInScreen: React.FC = () => {
  const navigate = hooks.useNavigate();
  const { t } = useTranslation();
  const { signInUser } = useAuthMethod();
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const { pathname } = useLocation();

  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
    };

    scrollToTop();
  }, [pathname]);

  const renderHeader = (): JSX.Element => {
    return <components.Header title={t("login")} goBack={true} />;
  };

  const validationSchema = yup.object({
    emailOrPhone: yup.string().required(t("emailOrPhoneRequired")),
    password: yup.string().required(t("passwordRequired")),
  });

  const [login, { loading }] = useMutation(LOGIN, {
    onCompleted: (data: any) => {
      if (data && data.loginMember) {
        signInUser(data.loginMember);
        navigate(APP_ROUTES.Home);
      }
    },
    onError(err) {
      showErrorToast(err.message);
    },
  });

  function handleSubmit(data: any) {
    login({
      variables: { emailOrPhone: data.emailOrPhone, password: data.password },
    });
  }

  const renderInputFields = (): JSX.Element => {
    return (
      <Formik
        validateOnChange
        initialValues={{ emailOrPhone: "", password: "" }}
        onSubmit={(data: any) => handleSubmit(data)}
        validationSchema={validationSchema}
      >
        {() => (
          <Form noValidate autoComplete="off">
            <Box>
              <AppTextField
                placeholder={t("emailOrPhone")}
                name="emailOrPhone"
                label={t("emailOrPhone")}
                themeColor="dark"
              />
              <AppTextField
                placeholder={t("password")}
                name="password"
                label={t("password")}
                themeColor="dark"
                type={showPassword ? "text" : "password"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        sx={{ color: "grey" }}
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon color="inherit" />
                        ) : (
                          <VisibilityIcon color="inherit" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Link
              to={APP_ROUTES.FORGOT_PASSWORD}
              style={{ textDecoration: "none" }}
            >
              <text.T16
                style={{
                  color: theme.colors.accentColor,
                  marginBottom: "10px",
                }}
              >
                {t("forgetPassword")}
              </text.T16>
            </Link>
            <AppSubmitButton
              label={t("login")}
              disabled={false}
              loading={loading}
            />
          </Form>
        )}
      </Formik>
    );
  };

  return (
    <main className="container login" style={{ paddingBottom: 50 }}>
      {renderHeader()}
      {renderInputFields()}
    </main>
  );
};
